import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

import { computed } from "vue";
import { ArtifactSchema } from "@/types";
import {
  artifactStore,
  permissionStore,
  selectionStore,
  subtreeStore,
  traceSaveStore,
  traceStore,
} from "@/hooks";
import {
  Typography,
  IconButton,
  PanelCard,
  TextButton,
} from "@/components/common";
import ArtifactListDisplay from "./ArtifactListDisplay.vue";


/**
 * Displays the selected node's parents and children.
 */
const __default__ = {
  name: "ArtifactTraces",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const displayActions = computed(() =>
  permissionStore.isAllowed("project.edit_data")
);

const artifact = computed(() => artifactStore.selectedArtifact);

const parents = computed(() =>
  artifact.value
    ? (subtreeStore
        .getParents(artifact.value.id)
        .map((id) => artifactStore.getArtifactById(id))
        .filter((artifact) => !!artifact) as ArtifactSchema[])
    : []
);

const children = computed(() =>
  artifact.value
    ? (subtreeStore
        .getChildren(artifact.value.id)
        .map((id) => artifactStore.getArtifactById(id))
        .filter((artifact) => !!artifact) as ArtifactSchema[])
    : []
);

const parentTitle = computed(() =>
  parents.value.length === 1
    ? "1 Parent Artifact"
    : `${parents.value.length} Parent Artifacts`
);

const childTitle = computed(() =>
  children.value.length === 1
    ? "1 Child Artifact"
    : `${children.value.length} Child Artifacts`
);

/**
 * Determines the className of the link to another artifact.
 * @param artifactName - The artifact to select the link to.
 * @returns The className for the link.
 */
function getTraceLinkClassName(artifactName: string): string {
  const relatedArtifact = artifactStore.getArtifactByName(artifactName);

  if (!relatedArtifact || !artifact.value) return "trace-chip text-nodeDefault";

  const traceLink = traceStore.getTraceLinkByArtifacts(
    relatedArtifact.id,
    artifact.value.id,
    true
  );

  const base =
    traceLink?.traceType === "GENERATED"
      ? "trace-chip-generated text-nodeGenerated "
      : "trace-chip text-nodeDefault ";
  const unreviewed =
    traceLink?.approvalStatus === "UNREVIEWED" ? "trace-chip-unreviewed" : "";

  return base + unreviewed;
}

/**
 * Selects an artifact.
 * @param artifactName - The artifact to select.
 */
function handleArtifactClick(artifactName: string): void {
  const relatedArtifact = artifactStore.getArtifactByName(artifactName);

  if (!relatedArtifact) return;

  selectionStore.selectArtifact(relatedArtifact.id);
}

/**
 * Selects the trace link to an artifact.
 * @param artifactName - The artifact to select the link to.
 */
function handleTraceLinkClick(artifactName: string): void {
  const relatedArtifact = artifactStore.getArtifactByName(artifactName);

  if (!relatedArtifact || !artifact.value) return;

  const traceLink = traceStore.getTraceLinkByArtifacts(
    relatedArtifact.id,
    artifact.value.id,
    true
  );

  if (!traceLink) return;

  selectionStore.selectTraceLink(traceLink);
}

/**
 * Opens the create trace link panel with this artifact as the child.
 */
function handleLinkParent(): void {
  if (!artifact.value) return;

  traceSaveStore.openPanel({
    type: "source",
    artifactId: artifact.value.id,
  });
}

/**
 * Opens the create trace link panel with this artifact as the parent.
 */
function handleLinkChild(): void {
  if (!artifact.value) return;

  traceSaveStore.openPanel({
    type: "target",
    artifactId: artifact.value.id,
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(PanelCard), {
      title: parentTitle.value,
      collapsable: "",
      borderless: ""
    }, {
      "title-actions": _withCtx(() => [
        (displayActions.value)
          ? (_openBlock(), _createBlock(_unref(TextButton), {
              key: 0,
              text: "",
              label: "Link Parent",
              icon: "add",
              "data-cy": "button-artifact-link-parent",
              onClick: handleLinkParent
            }))
          : _createCommentVNode("", true)
      ]),
      default: _withCtx(() => [
        (parents.value.length > 0)
          ? (_openBlock(), _createBlock(ArtifactListDisplay, {
              key: 0,
              "data-cy": "list-selected-parents",
              artifacts: parents.value,
              "action-cols": 1,
              "item-data-cy": "list-selected-parent-item",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (handleArtifactClick($event.name)))
            }, {
              actions: _withCtx(({ artifact: parent }) => [
                _createVNode(_unref(IconButton), {
                  outline: "",
                  flat: false,
                  icon: "trace",
                  tooltip: "View Trace Link",
                  "data-cy": "button-selected-parent-link",
                  class: _normalizeClass(getTraceLinkClassName(parent.name)),
                  onClick: ($event: any) => (handleTraceLinkClick(parent.name))
                }, null, 8, ["class", "onClick"])
              ]),
              _: 1
            }, 8, ["artifacts"]))
          : (_openBlock(), _createBlock(_unref(Typography), {
              key: 1,
              l: "1",
              variant: "caption",
              value: "There are no parent artifacts."
            }))
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(_unref(PanelCard), {
      title: childTitle.value,
      collapsable: "",
      borderless: ""
    }, {
      "title-actions": _withCtx(() => [
        (displayActions.value)
          ? (_openBlock(), _createBlock(_unref(TextButton), {
              key: 0,
              text: "",
              label: "Link Child",
              icon: "add",
              "data-cy": "button-artifact-link-child",
              onClick: handleLinkChild
            }))
          : _createCommentVNode("", true)
      ]),
      default: _withCtx(() => [
        (children.value.length > 0)
          ? (_openBlock(), _createBlock(ArtifactListDisplay, {
              key: 0,
              "data-cy": "list-selected-children",
              artifacts: children.value,
              "action-cols": 1,
              "item-data-cy": "list-selected-child-item",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (handleArtifactClick($event.name)))
            }, {
              actions: _withCtx(({ artifact: child }) => [
                _createVNode(_unref(IconButton), {
                  outline: "",
                  flat: false,
                  icon: "trace",
                  tooltip: "View Trace Link",
                  "data-cy": "button-selected-child-link",
                  class: _normalizeClass(getTraceLinkClassName(child.name)),
                  onClick: ($event: any) => (handleTraceLinkClick(child.name))
                }, null, 8, ["class", "onClick"])
              ]),
              _: 1
            }, 8, ["artifacts"]))
          : (_openBlock(), _createBlock(_unref(Typography), {
              key: 1,
              l: "1",
              variant: "caption",
              value: "There are no child artifacts."
            }))
      ]),
      _: 1
    }, 8, ["title"])
  ]))
}
}

})